// Apolo IDE — sessionGrouping (sprint code-session-board, E2-WS3).
//
// PURO: filtra e agrupa sessões por status derivado. Usado pelo filtro de chips
// e pela visão "board" (grupos por status). Atenção primeiro (o que precisa de
// você no topo), depois running, depois idle.
//
// sprint recents-claude-style: + agrupamento por PROJETO (pasta/repo) e por
// TEMPO (Hoje/Ontem/Esta semana/…) pros dois modos de visão da sidebar.

import { toMs } from './sessionMeta.js';

// Ordem de exibição no board/grupos: atenção primeiro.
export const BOARD_ORDER = ['blocked', 'review', 'needs_you', 'running', 'idle'];

// Timestamp de "última atividade" da sessão (updated_at > created_at). ms epoch.
function activityMs(s) {
  return toMs(s?.updated_at) ?? toMs(s?.created_at) ?? 0;
}

// Rótulo do projeto = basename da pasta/diretório da sessão. Sem pasta → grupo
// neutro (sessões de chat sem raiz). Normaliza barras finais.
export function projectLabel(session) {
  const dir = session?.directory || session?.root || session?.cwd || '';
  if (typeof dir === 'string' && dir.trim()) {
    const parts = dir.replace(/[/\\]+$/, '').split(/[/\\]/);
    const base = parts[parts.length - 1];
    if (base) return base;
  }
  return 'Sem projeto';
}

/**
 * Agrupa por PROJETO. Cada grupo ordenado por atividade desc; grupos ordenados
 * pela sessão mais recente de cada um. 'Sem projeto' sempre por último.
 * @returns [{ project, sessions }]
 */
export function groupSessionsByProject(sessions) {
  const list = Array.isArray(sessions) ? sessions : [];
  const byProject = new Map();
  for (const s of list) {
    const p = projectLabel(s);
    if (!byProject.has(p)) byProject.set(p, []);
    byProject.get(p).push(s);
  }
  const groups = [...byProject.entries()].map(([project, ss]) => ({
    project,
    sessions: ss.slice().sort((a, b) => activityMs(b) - activityMs(a)),
  }));
  groups.sort((a, b) => {
    if (a.project === 'Sem projeto') return 1;
    if (b.project === 'Sem projeto') return -1;
    return activityMs(b.sessions[0]) - activityMs(a.sessions[0]);
  });
  return groups;
}

// Baldes de tempo (estilo claude), em ordem de exibição.
const TIME_BUCKETS = [
  { key: 'today', label: 'Hoje' },
  { key: 'yesterday', label: 'Ontem' },
  { key: 'week', label: 'Esta semana' },
  { key: 'month', label: 'Este mês' },
  { key: 'older', label: 'Antes' },
];

function timeBucketKey(ms, now) {
  if (!ms) return 'older';
  const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
  const dayMs = 86400_000;
  if (ms >= startOfToday.getTime()) return 'today';
  if (ms >= startOfToday.getTime() - dayMs) return 'yesterday';
  if (ms >= startOfToday.getTime() - 7 * dayMs) return 'week';
  if (ms >= startOfToday.getTime() - 30 * dayMs) return 'month';
  return 'older';
}

/**
 * Agrupa por TEMPO (recência da última atividade), estilo claude. Só baldes
 * não-vazios, na ordem TIME_BUCKETS; sessões por atividade desc dentro do balde.
 * @returns [{ key, label, sessions }]
 */
export function groupSessionsByTime(sessions, now = Date.now()) {
  const list = Array.isArray(sessions) ? sessions : [];
  const buckets = new Map(TIME_BUCKETS.map((b) => [b.key, []]));
  for (const s of list) buckets.get(timeBucketKey(activityMs(s), now)).push(s);
  return TIME_BUCKETS
    .map((b) => ({ key: b.key, label: b.label, sessions: buckets.get(b.key).sort((a, z) => activityMs(z) - activityMs(a)) }))
    .filter((g) => g.sessions.length > 0);
}

function statusOf(statuses, id) {
  return statuses && statuses[id] ? statuses[id].status : undefined;
}

/**
 * @returns sessões cujo status === filter; filter 'all'/falsy → todas.
 */
export function filterSessionsByStatus(sessions, statuses, filter) {
  const list = Array.isArray(sessions) ? sessions : [];
  if (!filter || filter === 'all') return list;
  return list.filter((s) => statusOf(statuses, s.id) === filter);
}

/**
 * @returns [{ status, sessions }] na ordem de BOARD_ORDER, só grupos não-vazios.
 * Sessões sem status ainda no mapa ficam de fora (até o 1º poll preencher).
 */
export function groupSessionsByStatus(sessions, statuses) {
  const list = Array.isArray(sessions) ? sessions : [];
  const buckets = new Map(BOARD_ORDER.map((s) => [s, []]));
  for (const s of list) {
    const st = statusOf(statuses, s.id);
    if (st && buckets.has(st)) buckets.get(st).push(s);
  }
  return BOARD_ORDER
    .map((status) => ({ status, sessions: buckets.get(status) }))
    .filter((g) => g.sessions.length > 0);
}
